import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldX } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { sha256Hex } from "@/lib/utils/hash";
import { supabase } from "@/integrations/supabase/client";
import { formatDateTimeBR } from "@/lib/date-utils";

const coerceBoolean = (value: unknown): boolean | null => {
  if (value === true || value === false) return value;
  if (value == null) return null;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "t", "1", "yes", "y", "ok", "valid"].includes(normalized)) return true;
    if (["false", "f", "0", "no", "n", "invalid"].includes(normalized)) return false;
  }
  return null;
};

const parseInvokePayload = (payload: unknown): Record<string, unknown> => {
  if (payload && typeof payload === "object") return payload as Record<string, unknown>;
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload) as unknown;
      if (parsed && typeof parsed === "object") return parsed as Record<string, unknown>;
    } catch {
      // ignore
    }
  }
  return {};
};

export default function VerifyExport() {
  const { t } = useTranslation("medicalRecords");
  const [searchParams] = useSearchParams();
  const initialExportId = useMemo(
    () => searchParams.get("export_id") ?? "",
    [searchParams],
  );
  const [exportId, setExportId] = useState(initialExportId);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<"idle" | "checking" | "valid" | "invalid" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [details, setDetails] = useState<{
    generatedAt?: string | null;
    clinicName?: string | null;
    expectedHash?: string | null;
  } | null>(null);

  const handleVerify = async () => {
    setMessage(null);
    setDetails(null);

    if (!file || !exportId.trim()) {
      setResult("error");
      setMessage(t("verifyExport.requiredError"));
      return;
    }

    try {
      setResult("checking");
      const buffer = await file.arrayBuffer();
      const hash = await sha256Hex(buffer);

      const { data, error } = await supabase.functions.invoke("prontuario-export-verify", {
        body: {
          export_id: exportId.trim(),
          pdf_sha256: hash,
        },
      });

      if (error) {
        throw new Error(error.message ?? t("verifyExport.invokeError"));
      }

      const payload = parseInvokePayload(data);
      const expectedHash =
        (payload.expected_pdf_sha256 as string | undefined) ??
        (payload.expectedPdfSha256 as string | undefined) ??
        (payload.pdf_sha256 as string | undefined) ??
        null;

      const resolvedValid =
        coerceBoolean(payload.valid) ??
        coerceBoolean(payload.is_valid) ??
        coerceBoolean(payload.isValid) ??
        coerceBoolean(payload.verified) ??
        (expectedHash ? expectedHash.toLowerCase() === hash.toLowerCase() : null) ??
        false;

      const valid = resolvedValid === true;
      setResult(valid ? "valid" : "invalid");
      setMessage(
        valid ? t("verifyExport.valid") : t("verifyExport.invalid"),
      );
      setDetails({
        generatedAt:
          (payload.generated_at as string | undefined) ??
          (payload.generatedAt as string | undefined) ??
          null,
        clinicName:
          (payload.clinic_name as string | undefined) ??
          (payload.clinicName as string | undefined) ??
          null,
        expectedHash,
      });
    } catch (error) {
      console.error("Erro ao verificar exportação", error);
      setResult("error");
      setMessage(t("verifyExport.genericError"));
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>{t("verifyExport.title")}</CardTitle>
          <CardDescription>
            {t("verifyExport.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="export-id">{t("verifyExport.exportId")}</Label>
            <Input
              id="export-id"
              value={exportId}
              onChange={(event) => setExportId(event.target.value)}
              placeholder={t("verifyExport.exportIdPlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="export-file">{t("verifyExport.pdf")}</Label>
            <Input
              id="export-file"
              type="file"
              accept="application/pdf"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </div>
          <Button onClick={handleVerify} disabled={result === "checking"}>
            {result === "checking" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("actions.verifying")}
              </>
            ) : (
              t("actions.verify")
            )}
          </Button>

          {message ? (
            <div
              className={
                result === "valid"
                  ? "text-sm text-emerald-600"
                  : result === "invalid" || result === "error"
                    ? "text-sm text-destructive"
                    : "text-sm text-muted-foreground"
              }
            >
              {result === "valid" ? (
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  {message}
                </span>
              ) : result === "invalid" || result === "error" ? (
                <span className="inline-flex items-center gap-2">
                  <ShieldX className="h-4 w-4" />
                  {message}
                </span>
              ) : (
                message
              )}
            </div>
          ) : null}

          {details ? (
            <div className="text-xs text-muted-foreground space-y-1">
              {details.generatedAt ? (
                <div>{t("verifyExport.generatedAt")}: {formatDateTimeBR(details.generatedAt)}</div>
              ) : null}
              {details.clinicName ? <div>{t("verifyExport.clinic")}: {details.clinicName}</div> : null}
              {details.expectedHash ? (
                <div className="font-mono break-all">{t("verifyExport.expectedHash")}: {details.expectedHash}</div>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
